import React, { useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  SimpleGrid,
  Tag,
  TagLabel,
  Text,
  Tooltip,
  VStack,
  useToast,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import { getData, postData } from '../../../utils/httpClient';

function downloadBase64File({ filename, mimeType, contentBase64 }) {
  const byteCharacters = atob(contentBase64 || '');
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: mimeType || 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'reporte';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

const EXPORTS = [
  { key: 'students', label: 'Estudiantes' },
  { key: 'employees', label: 'Empleados' },
  { key: 'accounts', label: 'Cuentas corrientes' },
  { key: 'payrolls', label: 'Liquidaciones' },
  { key: 'subjects', label: 'Materias' },
  { key: 'dictated-courses', label: 'Dictados' },
  { key: 'course-results', label: 'Resultados de dictados (actas)' },
  { key: 'exam-schedules', label: 'Exámenes programados' },
  { key: 'exam-inscriptions', label: 'Inscripciones a exámenes' },
  { key: 'exam-results', label: 'Resultados de exámenes (actas)' },
  { key: 'classrooms', label: 'Aulas' },
  { key: 'course-schedules', label: 'Horarios de cursos' },
];

// Compatibilidades para habilitar selecciones dinámicas.
// Nota: esto controla la UI; el backend decide qué reporte combinado generar.
const COMBINE_GRAPH = {
  students: ['dictated-courses', 'course-results', 'exam-results', 'exam-inscriptions', 'exam-schedules', 'subjects', 'accounts'],
  'dictated-courses': ['students', 'course-results', 'subjects', 'course-schedules'],
  'course-results': ['students', 'dictated-courses', 'subjects'],
  'exam-results': ['students', 'exam-inscriptions', 'exam-schedules', 'subjects'],
  'exam-inscriptions': ['students', 'exam-schedules', 'subjects', 'exam-results'],
  'exam-schedules': ['students', 'exam-inscriptions', 'subjects', 'exam-results'],
  subjects: ['students', 'dictated-courses', 'course-results', 'exam-results', 'exam-schedules', 'exam-inscriptions'],
  accounts: ['students'],
};

export default function ReportsView() {
  const toast = useToast();
  const [downloading, setDownloading] = useState(null);
  const [selected, setSelected] = useState({});
  const [combinedSelected, setCombinedSelected] = useState({ students: true });

  const exportLabelsByKey = useMemo(() => {
    const map = {};
    for (const e of EXPORTS) map[e.key] = e.label;
    return map;
  }, []);

  const selectedKeys = useMemo(
    () => EXPORTS.filter((e) => !!selected[e.key]).map((e) => e.key),
    [selected],
  );

  const combinedKeys = useMemo(
    () => Object.keys(combinedSelected).filter((k) => !!combinedSelected[k]),
    [combinedSelected],
  );

  const allowedCombinedKeys = useMemo(() => {
    const keys = combinedKeys.length ? combinedKeys : [];
    if (!keys.length) return new Set(['students']);
    const allowed = new Set(keys);
    for (const k of keys) {
      const next = COMBINE_GRAPH[k] || [];
      for (const n of next) allowed.add(n);
    }
    // Siempre permitimos volver a activar students (base más común)
    allowed.add('students');
    return allowed;
  }, [combinedKeys]);

  const exportXlsx = async (entity) => {
    setDownloading(entity);
    try {
      const res = await getData(`/university-ops/export/${entity}/xlsx`);
      const payload = res?.payload;
      if (!payload?.contentBase64) throw new Error('Respuesta inválida del servidor');
      downloadBase64File(payload);
      toast({ title: 'Descarga iniciada', status: 'success', duration: 2500, isClosable: true });
    } catch (e) {
      toast({
        title: 'No se pudo exportar',
        description: e?.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setDownloading(null);
    }
  };

  const exportCombined = async () => {
    if (!selectedKeys.length) {
      toast({ title: 'Seleccione al menos un reporte', status: 'warning', duration: 2500 });
      return;
    }
    setDownloading('__combined__');
    try {
      const res = await postData('/university-ops/export/xlsx', { entities: selectedKeys });
      const payload = res?.payload;
      if (!payload?.contentBase64) throw new Error('Respuesta inválida del servidor');
      downloadBase64File(payload);
      toast({ title: 'Descarga iniciada', status: 'success', duration: 2500, isClosable: true });
    } catch (e) {
      toast({
        title: 'No se pudo exportar',
        description: e?.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setDownloading(null);
    }
  };

  const exportDynamicCombined = async () => {
    const keys = combinedKeys;
    if (!keys.length) {
      toast({ title: 'Seleccione al menos una entidad', status: 'warning', duration: 2500 });
      return;
    }
    if (!keys.includes('students')) {
      toast({ title: 'Por ahora la base debe incluir Estudiantes', status: 'warning', duration: 2500 });
      return;
    }

    setDownloading(`__dynamic_combined__`);
    try {
      const res = await postData(`/university-ops/reports/xlsx`, { entities: keys });
      const payload = res?.payload;
      if (!payload?.contentBase64) throw new Error('Respuesta inválida del servidor');
      downloadBase64File(payload);
      toast({ title: 'Descarga iniciada', status: 'success', duration: 2500, isClosable: true });
    } catch (e) {
      toast({
        title: 'No se pudo exportar',
        description: e?.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setDownloading(null);
    }
  };

  const combinedSelectedLabels = useMemo(
    () => combinedKeys.map((k) => exportLabelsByKey[k] || k),
    [combinedKeys, exportLabelsByKey],
  );

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Card direction="column" w="100%" px="25px" py="20px">
        <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={3}>
          <Box>
            <Text fontSize="22px" fontWeight="700">
              Reportes y exportaciones
            </Text>
            <Text color="gray.500" fontSize="sm">
              Descargá información de la app en Excel, o armá un reporte combinado en una sola hoja.
            </Text>
          </Box>
        </Flex>

        <Box mb={6} borderWidth="1px" borderRadius="lg" p={4} bg="gray.50">
          <Flex justify="space-between" align="flex-start" flexWrap="wrap" gap={3} mb={3}>
            <Box>
              <Flex align="center" gap={2} mb={1}>
                <Badge colorScheme="teal">Nuevo</Badge>
                <Text fontSize="md" fontWeight="800">
                  Constructor de reporte combinado
                </Text>
              </Flex>
              <Text color="gray.600" fontSize="sm">
                Elegí una entidad base y agregá entidades compatibles. Se genera un Excel de <b>una sola hoja</b>.
              </Text>
            </Box>
            <Button
              colorScheme="teal"
              size="sm"
              onClick={exportDynamicCombined}
            >
              Descargar combinado (1 hoja)
            </Button>
          </Flex>

          <Box mb={3}>
            <Text fontSize="sm" fontWeight="700" mb={2} color="gray.700">
              Selección actual
            </Text>
            <Flex wrap="wrap" gap={2}>
              {combinedSelectedLabels.map((label) => (
                <Tag key={label} size="sm" colorScheme="teal" borderRadius="full">
                  <TagLabel>{label}</TagLabel>
                </Tag>
              ))}
            </Flex>
          </Box>

          <Divider my={3} />

          <Text fontSize="sm" fontWeight="700" mb={2} color="gray.700">
            Entidades (se habilitan según compatibilidad)
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={2}>
            {EXPORTS.filter((e) =>
              ['students', 'dictated-courses', 'course-results', 'exam-results', 'exam-inscriptions', 'exam-schedules', 'subjects', 'accounts'].includes(
                e.key,
              ),
            ).map((e) => {
              const isAllowed = allowedCombinedKeys.has(e.key);
              const isChecked = !!combinedSelected[e.key];
              const checkbox = (
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  px={3}
                  py={2}
                  bg={isChecked ? 'white' : 'transparent'}
                  opacity={isAllowed ? 1 : 0.55}
                >
                  <Checkbox
                    isChecked={isChecked}
                    isDisabled={!isAllowed}
                    onChange={(ev) =>
                      setCombinedSelected((p) => {
                        const next = { ...p, [e.key]: ev.target.checked };
                        if (!Object.values(next).some(Boolean)) return { students: true };
                        return next;
                      })
                    }
                  >
                    <Text fontWeight="700" fontSize="sm">
                      {e.label}
                    </Text>
                    <Text color="gray.500" fontSize="xs">
                      {e.key === 'students'
                        ? 'Entidad base recomendada'
                        : isAllowed
                          ? 'Compatible con tu selección'
                          : 'No compatible (seleccioná otra base)'}
                    </Text>
                  </Checkbox>
                </Box>
              );

              if (isAllowed) return <Box key={e.key}>{checkbox}</Box>;

              return (
                <Tooltip key={e.key} label="Esta entidad no se puede combinar con tu selección actual." placement="top" hasArrow>
                  <Box>{checkbox}</Box>
                </Tooltip>
              );
            })}
          </SimpleGrid>
        </Box>

        <Divider my={6} />

        <Flex justify="space-between" align="center" mb={3} flexWrap="wrap" gap={3}>
          <Box>
            <Text fontSize="md" fontWeight="800">
              Exportaciones rápidas (una hoja por entidad)
            </Text>
            <Text color="gray.500" fontSize="sm">
              Seleccioná varias para descargarlas en un solo Excel con múltiples hojas.
            </Text>
          </Box>
          <Button
            colorScheme="purple"
            size="sm"
            onClick={exportCombined}
          >
            Descargar Excel multi-hoja
          </Button>
        </Flex>

        <VStack align="stretch" spacing={3}>
          {EXPORTS.map((e) => (
            <Flex key={e.key} justify="space-between" align="center" borderWidth="1px" borderRadius="md" p={3}>
              <Flex align="center" gap={3}>
                <Checkbox
                  isChecked={!!selected[e.key]}
                  onChange={(ev) => setSelected((p) => ({ ...p, [e.key]: ev.target.checked }))}
                />
                <Text fontWeight="600">{e.label}</Text>
              </Flex>
              <Button
                colorScheme="blue"
                size="sm"
                onClick={() => exportXlsx(e.key)}
              >
                Descargar Excel
              </Button>
            </Flex>
          ))}
        </VStack>
      </Card>
    </Box>
  );
}

